import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, MapPin, Users, Globe, ArrowLeft, Briefcase,
  Loader2, ExternalLink, ChevronRight, Linkedin, Instagram, Calendar, CheckCircle
} from "lucide-react";
import JobCard from "../components/JobCard";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { industryLabel } from "../utils/industryLabel";
import { companySizeLabel } from "../utils/companySize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prefers structured city/región/país (Fase 3) — falls back to the legacy
// free-text `location` string for older company records, never drops data.
function structuredLocationLabel(company, t) {
  if (!company?.city) return null;
  const parts = [company.city];
  if (company.admin1_name) parts.push(company.admin1_name);
  if (company.country_code) parts.push(t(`geo.countryNames.${company.country_code}`));
  return parts.join(", ");
}

export default function CompanyProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/companies/${id}`, { headers: getAuthHeaders() });
        setCompany(res.data);
      } catch { navigate("/companies"); }
      finally { setLoading(false); }
    };
    load();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div data-testid="company-profile-page" className="min-h-screen bg-white">

      {/* ── Company Header ── */}
      <section className="hero-infocity py-5 sm:py-10">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:underline text-sm mb-3 sm:mb-5 transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("companyProfile.back")}
          </button>

          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            {/* Company logo */}
            <div className="h-14 w-14 sm:h-20 sm:w-20 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center font-poppins font-bold text-white text-xl sm:text-3xl flex-shrink-0 overflow-hidden" data-testid="company-profile-avatar">
              {company.logo_url ? (
                <img src={company.logo_url} alt={company.name} className="w-full h-full object-cover" />
              ) : (
                company.name?.[0]?.toUpperCase()
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white uppercase tracking-widest mb-0.5 sm:mb-1 truncate">{industryLabel(company.industry, t) || t("companyProfile.companyFallback")}</p>
              <h1 className="font-poppins text-xl sm:text-3xl font-bold text-white mb-1.5 sm:mb-3 break-words" data-testid="company-name">
                {company.name}
              </h1>
              <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-white">
                {(structuredLocationLabel(company, t) || company.location) && (
                  <span className="flex items-center gap-1.5 break-words max-w-full" data-testid="company-profile-location">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />{structuredLocationLabel(company, t) || company.location}
                  </span>
                )}
                {company.size && (
                  <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 flex-shrink-0" />{companySizeLabel(company.size, t)}</span>
                )}
                {company.founded_year && (
                  <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 flex-shrink-0" />{t("companyProfile.foundedLabel")} {company.founded_year}</span>
                )}
                {company.website && (
                  <a href={company.website} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-white hover:underline transition-colors">
                    <Globe className="h-3.5 w-3.5" /> {t("companyProfile.websiteLabel")}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {company.linkedin_url && (
                  <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-white hover:underline transition-colors">
                    <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                  </a>
                )}
                {company.instagram_url && (
                  <a href={company.instagram_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-white hover:underline transition-colors">
                    <Instagram className="h-3.5 w-3.5" /> Instagram
                  </a>
                )}
              </div>
            </div>

            {/* Stats in header */}
            <div className="flex gap-5 sm:flex-col sm:gap-3 sm:text-right flex-shrink-0">
              <div>
                <p className="font-poppins text-xl sm:text-2xl font-bold text-white">{company.job_count || 0}</p>
                <p className="text-xs text-white">{t("companyProfile.openPositions")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main: Job Listings */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-2">{t("companyProfile.opportunities")}</p>
              <h2 className="font-poppins text-2xl font-bold text-slate-900 mb-1">
                {t("companyProfile.openPositions")}
                <span className="ml-2 text-base font-normal text-slate-400">({company.jobs?.length || 0})</span>
              </h2>
            </div>

            {company.jobs?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="company-jobs">
                {company.jobs.map((job) => <JobCard key={job.job_id} job={job} />)}
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-10 text-center" data-testid="company-jobs">
                <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="font-poppins font-semibold text-slate-600 mb-1">{t("companyProfile.noJobs")}</p>
                <p className="text-sm text-slate-500">{t("companyProfile.noJobsDesc")}</p>
              </div>
            )}
          </div>

          {/* Sidebar: Company Info */}
          <div className="space-y-5">
            {/* About */}
            {company.description && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="company-description">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3">{t("companyProfile.about")}</p>
                <p className="text-sm text-slate-600 leading-relaxed break-words">{company.description}</p>
              </div>
            )}

            {/* Culture (Fase 3, sept-2026) */}
            {company.culture && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="company-culture">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3">{t("companyProfile.cultureLabel")}</p>
                <p className="text-sm text-slate-600 leading-relaxed break-words">{company.culture}</p>
              </div>
            )}

            {/* Benefits (Fase 3, sept-2026) */}
            {company.benefits?.filter(Boolean).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="company-benefits">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3">{t("companyProfile.benefitsLabel")}</p>
                <ul className="space-y-2">
                  {company.benefits.filter(Boolean).map((b, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-[#028CF8] mt-0.5 flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Company details */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="company-info-sidebar">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-4">{t("companyProfile.companyInfo")}</p>
              <div className="space-y-3.5">
                {[
                  { icon: Building2, label: t("companyProfile.industryLabel"), value: industryLabel(company.industry, t) },
                  { icon: Users, label: t("companyProfile.sizeLabel"), value: companySizeLabel(company.size, t) },
                  { icon: MapPin, label: t("companyProfile.hqLabel"), value: structuredLocationLabel(company, t) || company.location },
                  { icon: Briefcase, label: t("companyProfile.activeJobsLabel"), value: `${company.job_count || 0}` },
                ].filter((x) => x.value).map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-3.5 w-3.5 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400 truncate">{label}</p>
                      <p className="font-semibold text-slate-900 text-sm break-words">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
              {(company.latitude != null || company.address || company.location) && (
                <a
                  data-testid="company-get-directions-button"
                  href={
                    company.latitude != null && company.longitude != null
                      ? `https://www.google.com/maps/dir/?api=1&destination=${company.latitude},${company.longitude}`
                      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(company.address || company.location)}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 w-full h-10 border border-[#028CF8] text-[#028CF8] text-sm font-semibold rounded-xl hover:bg-[#028CF8]/5 transition-all flex items-center justify-center gap-2"
                >
                  <MapPin className="h-4 w-4" /> {t("companyProfile.getDirections")}
                </a>
              )}
              {company.website && (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full h-10 bg-[#028CF8] text-white text-sm font-semibold rounded-xl hover:bg-[#0270C5] transition-all flex items-center justify-center gap-2"
                >
                  <Globe className="h-4 w-4" /> {t("companyProfile.visitWebsite")}
                </a>
              )}
            </div>

            {/* CTA: View all jobs */}
            <div className="bg-gradient-to-br from-[#028CF8] to-[#0270C5] rounded-2xl p-5 text-white">
              <p className="font-poppins font-bold mb-1">{t("companyProfile.lookingForJob")}</p>
              <p className="text-white text-xs mb-4 break-words">
                {t("companyProfile.browseAllDesc").replace("{count}", company.job_count || 0).replace("{name}", company.name)}
              </p>
              <Link
                to="/jobs"
                className="flex items-center justify-between text-sm font-semibold text-white bg-white/15 border border-white/20 rounded-xl px-4 py-2.5 hover:bg-white/25 transition-all"
              >
                {t("companyProfile.browseAll")} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
